import React, { useState, useMemo, useCallback } from 'react';
import { Card, Tooltip, Space, Spin, message } from 'antd';
import {
  CopyOutlined,
  LockOutlined,
  UnlockOutlined,
  FileSearchOutlined,
} from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import { useSocketContext } from '../../../sockets/SocketContext';
import { similarBrandSearchStart } from '../../../store/brandNameSlice';
import { similarAnalogSearchStart } from '../../../store/analogGeneratorSlice';
import DocumentModal from '../../TZ/ext/DocumentModal';
import FavoriteCard from './FavoriteCard';
import { getImageSrc } from '../../../utils/helpers';

const defaultSimilar = { loading: false, error: null, data: null };

const VARIANT = {
  brand: {
    sliceKey: 'brandName',
    startAction: similarBrandSearchStart,
    socketField: 'similar_brand',
    socketType: 'similar_brand',
    searchTooltip: 'Поиск похожих названий',
    emptyText: 'Похожих названий не найдено',
    createError: 'Ошибка создания задачи для поиска похожих названий',
    favoriteRequestType: 'similar_brand',
  },
  analog: {
    sliceKey: 'analogGenerator',
    startAction: similarAnalogSearchStart,
    socketField: 'similar_analog',
    socketType: 'similar_analog',
    searchTooltip: 'Поиск похожих аналогов',
    emptyText: 'Похожих аналогов не найдено',
    createError: 'Ошибка создания задачи для поиска похожих аналогов',
    favoriteRequestType: 'similar_analog',
  },
};

/**
 * @param {'brand'|'analog'} variant
 * @param {{ id: string, text: string, locked?: boolean }} item
 */
function SimilarSuggestionCard({ variant, item, onToggleLock }) {
  const cfg = VARIANT[variant];
  const dispatch = useDispatch();
  const { createNewTaskOnServer } = useSocketContext();

  const similarResult = useSelector((state) => {
    const slice = state[cfg.sliceKey];
    return slice?.similar?.[item.id] || defaultSimilar;
  });

  const [selectedDoc, setSelectedDoc] = useState(null);
  const [modalVisible, setModalVisible] = useState(false);

  const handleSearchSimilar = useCallback(() => {
    dispatch(cfg.startAction(item.id));
    createNewTaskOnServer(
      { [cfg.socketField]: item.text, quantity: 5, suggestionId: item.id },
      cfg.socketType,
      (err) => {
        if (err) message.error(cfg.createError);
      },
    );
  }, [cfg, createNewTaskOnServer, dispatch, item.id, item.text]);

  const handleMiniCardClick = useCallback((docItem) => {
    setSelectedDoc(docItem);
    setModalVisible(true);
  }, []);

  const cardStyle = useMemo(
    () => ({
      marginBottom: 10,
      ...(item.locked && { border: '2px solid #52c41a', backgroundColor: 'var(--theme-success-bg)' }),
    }),
    [item.locked],
  );

  const listKey = useCallback((entry, idx) => entry?.document?.id ?? entry?.id ?? idx, []);

  return (
    <>
      <Card style={cardStyle}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ flex: 1 }}>{item.text}</div>
          <Space>
            <Tooltip title="Скопировать">
              <CopyOutlined
                style={{ color: '#1890ff', cursor: 'pointer' }}
                onClick={() => navigator.clipboard.writeText(item.text)}
              />
            </Tooltip>
            <Tooltip title={item.locked ? 'Разблокировать' : 'Заблокировать'}>
              {item.locked ? (
                <UnlockOutlined
                  style={{ color: '#1890ff', cursor: 'pointer' }}
                  onClick={() => onToggleLock(item)}
                />
              ) : (
                <LockOutlined
                  style={{ color: '#1890ff', cursor: 'pointer' }}
                  onClick={() => onToggleLock(item)}
                />
              )}
            </Tooltip>
            <Tooltip title={cfg.searchTooltip}>
              <FileSearchOutlined
                style={{ color: '#1890ff', cursor: 'pointer' }}
                onClick={handleSearchSimilar}
              />
            </Tooltip>
          </Space>
        </div>
        {similarResult.loading && (
          <div style={{ marginTop: 10 }}>
            <Spin size="small" />
          </div>
        )}
        {!similarResult.loading && similarResult.data !== null && similarResult.data.length === 0 && (
          <div style={{ marginTop: 10, color: '#999' }}>{cfg.emptyText}</div>
        )}
        {similarResult.data !== null && similarResult.data.length > 0 && (
          <div
            style={{
              display: 'flex',
              gap: 12,
              marginTop: 10,
              flexWrap: 'nowrap',
              overflowX: 'auto',
              overflowY: 'hidden',
              paddingBottom: 8,
              scrollSnapType: 'x proximity',
              WebkitOverflowScrolling: 'touch',
            }}
          >
            {similarResult.data.map((entry, idx) => {
              const imgSrc = getImageSrc(entry.base64Image);
              const k = listKey(entry, idx);
              return (
                <div
                  key={k}
                  style={{ flex: '0 0 auto', scrollSnapAlign: 'start' }}
                >
                  <FavoriteCard
                    document={entry.document}
                    imgSrc={imgSrc}
                    onClick={() => handleMiniCardClick(entry)}
                    score={entry.score || 0}
                    searchQuery={item.text}
                    request_type={cfg.favoriteRequestType}
                  />
                </div>
              );
            })}
          </div>
        )}
        {similarResult.error && (
          <div style={{ marginTop: 10, color: 'red' }}>{similarResult.error}</div>
        )}
      </Card>
      {modalVisible && selectedDoc && (
        <DocumentModal
          open={modalVisible}
          onClose={() => setModalVisible(false)}
          doc={selectedDoc}
          imgSrc={getImageSrc(selectedDoc.base64Image)}
        />
      )}
    </>
  );
}

export default React.memo(SimilarSuggestionCard);
